import { SalesSection } from '@/components/metrics/sales-section';
import { CampaignsSection } from '@/components/metrics/campaigns-section';
import { UsersSection } from '@/components/metrics/users-section';
import { SalesKpiCarousel } from '@/components/metrics/kpi-carousel';
import type { OpenDrilldown } from '@/components/metrics/metric-sections.types';
import type { AnalyticsTabParams } from '@/hooks/analytics/analytics-queries';
import { useSalesTabData } from '@/hooks/analytics/use-sales-tab-data';
import { useCampaignsTabData } from '@/hooks/analytics/use-campaigns-tab-data';
import { useUsersTabData } from '@/hooks/analytics/use-users-tab-data';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  currencyForDisplay,
  useStoreCurrency,
} from '@/hooks/use-store-currency';
import type { ChartRange } from '@/lib/partial-buckets';
import { DateTime } from 'luxon';
import { ComparisonLabel } from '@/components/metrics/delta-badge';

export interface AnalyticsTabPanelProps extends AnalyticsTabParams {
  dateFilterLabel: string;
  /** Absent when analytics is off (drill-downs need real data). */
  openDrilldown?: OpenDrilldown;
}

const chartRangeOf = ({
  fromDate,
  toDate,
}: AnalyticsTabParams): ChartRange | undefined =>
  fromDate && toDate ? { from: fromDate, to: toDate } : undefined;

/** Vendas (ANLT-04): mounted only while its tab is open (ANLT-01). */
export function SalesTabPanel(props: AnalyticsTabPanelProps) {
  const isMobile = useIsMobile();
  const {
    salesMetrics,
    salesChart,
    previousSalesChart,
    aovChart,
    repurchaseChart,
    ordersCompletion,
    deltas,
  } = useSalesTabData(props);
  const currency = salesMetrics.data?.currency ?? 'USD';

  return (
    <SalesSection
      salesMetrics={salesMetrics.data}
      isLoadingSalesMetrics={salesMetrics.isLoading}
      currency={currency}
      granularity={props.granularity}
      chartRange={chartRangeOf(props)}
      salesChart={salesChart.data}
      aovChart={aovChart.data}
      repurchaseChart={repurchaseChart.data}
      ordersCompletion={ordersCompletion.data}
      isLoadingSalesChart={salesChart.isLoading}
      isLoadingAovChart={aovChart.isLoading}
      isLoadingRepurchaseChart={repurchaseChart.isLoading}
      isLoadingOrdersCompletion={ordersCompletion.isLoading}
      openDrilldown={props.openDrilldown}
      previousSalesChart={previousSalesChart.data}
      deltas={deltas}
      comparison={
        <ComparisonLabel preset={props.preset} now={DateTime.now()} />
      }
      kpis={
        // The desktop has no sales indicators row (SALES-04 AC 3).
        isMobile ? (
          <div className="mt-4">
            <SalesKpiCarousel
              salesMetrics={salesMetrics.data}
              isLoadingSalesMetrics={salesMetrics.isLoading}
              dateFilterLabel={props.dateFilterLabel}
              currency={currency}
            />
          </div>
        ) : undefined
      }
    />
  );
}

/** Push (ANLT-05): mounted only while its tab is open (ANLT-01). */
export function CampaignsTabPanel(props: AnalyticsTabPanelProps) {
  const {
    campaignPerformance,
    campaignOrdersChart,
    dataSufficiency,
    periodCampaigns,
  } = useCampaignsTabData(props);
  // The Push tab does not fetch /metrics/sales, where the other tabs read the
  // store currency from.
  const currency = currencyForDisplay(useStoreCurrency()) ?? 'USD';

  return (
    <CampaignsSection
      campaignPerformance={campaignPerformance.data}
      isLoadingCampaignPerformance={campaignPerformance.isLoading}
      isLoadingDataSufficiency={dataSufficiency.isLoading}
      dateFilterLabel={props.dateFilterLabel}
      currency={currency}
      campaignsSufficiency={dataSufficiency.data?.campaignsAnalytics}
      granularity={props.granularity}
      chartRange={chartRangeOf(props)}
      periodCampaigns={periodCampaigns.data?.data}
      campaignOrdersChart={campaignOrdersChart.data}
      isLoadingCampaignOrdersChart={campaignOrdersChart.isLoading}
      openDrilldown={props.openDrilldown}
    />
  );
}

/** Usuários (ANLT-06): mounted only while its tab is open (ANLT-01). */
export function UsersTabPanel(props: AnalyticsTabPanelProps) {
  const {
    userMetrics,
    downloadsDelta,
    appDownloadsChart,
    appSessionsChart,
    appDownloadsCumulativeChart,
    installedVersions,
    dataSufficiency,
  } = useUsersTabData(props);

  return (
    <UsersSection
      userMetrics={userMetrics.data}
      isLoadingUserMetrics={userMetrics.isLoading}
      isLoadingDataSufficiency={dataSufficiency.isLoading}
      dateFilterLabel={props.dateFilterLabel}
      activeUsersSufficiency={dataSufficiency.data?.activeUsersChart}
      granularity={props.granularity}
      chartRange={chartRangeOf(props)}
      appDownloadsChart={appDownloadsChart.data}
      appDownloadsCumulativeChart={appDownloadsCumulativeChart.data}
      isLoadingAppDownloadsChart={appDownloadsChart.isLoading}
      isLoadingAppDownloadsCumulativeChart={
        appDownloadsCumulativeChart.isLoading
      }
      downloadsDelta={downloadsDelta}
      comparison={
        <ComparisonLabel preset={props.preset} now={DateTime.now()} />
      }
      appSessionsChart={appSessionsChart.data}
      isLoadingAppSessionsChart={appSessionsChart.isLoading}
      installedVersions={installedVersions.data}
    />
  );
}
